import { memo } from 'react';
import type { PricedItem } from '@/app/types/item';
import { formatInr, formatQuantity, priceLadder, resolveRate } from '@/app/lib/pricing';
import { MobileQuantityStepper } from '@/components/ui/MobileQuantityStepper';
import { cn } from '@/lib/utils';

interface ProductRowProps {
    item: PricedItem;
    quantity: number;
    onQuantityChange: (item: PricedItem, quantity: number) => void;
    /** Quantity on this party's most recent invoice, shown as a hint. */
    lastQuantity?: number;
    /** Opens the full price ladder for this item. */
    onShowTiers: (item: PricedItem) => void;
}

/**
 * One product line in the order builder.
 *
 * The rate is rendered as muted, non-interactive text — it is the server's quoted
 * rate for this quantity, and managers cannot override it. When a tier
 * applies, the merchant's own label ("BUY 100+ pc @") is shown beside it so the
 * price reads as deliberate rather than arbitrary.
 */
function ProductRowBase({
    item,
    quantity,
    onQuantityChange,
    lastQuantity,
    onShowTiers,
}: ProductRowProps) {
    // Preview the rate at qty 1 when nothing is selected, so the row always shows a price.
    const { rate, tierLabel, priced } = resolveRate(item, quantity > 0 ? quantity : 1);
    const selected = quantity > 0;
    const lineTotal = rate * quantity;
    const unpriced = !priced;

    // The chip is the signal that this item has bulk rates at all — the numbers
    // themselves live in the sheet, so rows stay scannable. It is shown whenever
    // a ladder exists, reached or not, so it does not flicker as quantity changes.
    const hasBulkPricing = !unpriced && priceLadder(item, quantity).length > 0;

    return (
        <li
            className={cn(
                'flex items-center gap-3 px-4 py-3 min-h-[72px] transition-colors',
                selected && 'bg-primary/5'
            )}
        >
            <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground leading-snug break-words">
                    {item.name}
                </p>

                <div className="flex items-center gap-x-2 gap-y-0.5 mt-1 flex-wrap">
                    {unpriced ? (
                        <span className="text-xs text-destructive">No price configured</span>
                    ) : (
                        <span className="text-sm text-muted-foreground tabular-nums">
                            {formatInr(rate)} / {item.unitName}
                        </span>
                    )}

                    {tierLabel && (
                        <span className="text-[11px] font-medium text-green-500 whitespace-nowrap">
                            {tierLabel}
                        </span>
                    )}

                    {!selected && lastQuantity ? (
                        <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                            last: {formatQuantity(lastQuantity)}
                        </span>
                    ) : null}

                    {hasBulkPricing && (
                        <button
                            type="button"
                            onClick={() => onShowTiers(item)}
                            className="text-[11px] font-medium text-green-500 border border-green-500/40 rounded-full px-2 py-0.5 whitespace-nowrap active:bg-green-500/10"
                        >
                            Bulk pricing
                        </button>
                    )}
                </div>

                {selected && (
                    <p className="text-sm font-semibold text-foreground mt-1 tabular-nums">
                        {formatInr(lineTotal)}
                    </p>
                )}
            </div>

            <MobileQuantityStepper
                value={quantity}
                onValueChange={(q) => onQuantityChange(item, q)}
                disabled={unpriced}
                aria-label={item.name}
            />
        </li>
    );
}

export const ProductRow = memo(ProductRowBase);
